/**
 * Denormalize orders for export — fan out each order's items[] into one row per SKU,
 * mirroring the import format (one CSV row per Order # + SKU).
 *
 * Without this rule, exporting orders produces one row per order with items[] as a
 * nested JSON blob — not round-trippable through the importer.
 *
 * Pattern follows PR 3469.
 */
import { dbViews } from 'lib_ui-services';
import basicWillExport from '../../namespace/relation/export_namespace_relation/willExport_namespace_relation';
import { format } from 'date-fns';

const { getDbView } = dbViews;

const _p = { getDbView };
export const _private = _p;

export default {
    verb: 'willExport',
    namespace: 'order',
    relation: 'order',
    description: 'Denormalize orders into one row per SKU for export',
    // Run AFTER the default willExport so filename/columns we set here win
    priority: (basicWillExport.priority || 0) - 5,
    featureFlag: 'orderConfirmation_enabled',
    logic: willExport
};

async function willExport(payload) {
    const { data, context: { namespace, relation } } = payload;

    // Source records: prefer upstream-prepared data.data, then dbViewData, then fetch from view.
    let records = data.data;
    if (!records) records = data.dbViewData;
    if (!records) {
        const view = await _p.getDbView(namespace, relation, data.viewName);
        records = view?.data;
    }
    records = records || [];

    const denormalizedRecords = records.flatMap(order => {
        // Preserve _id on every emitted row so doingExport's selection-filter (which
        // matches by record._id) continues to work — multiple rows sharing the same
        // _id all pass/fail selection together, which is the right behavior.
        const orderFields = {
            _id: order._id,
            title: order.title,
            'order:status': order['order:status']?.title || '',
            'location:location': order['location:location']?.title || '',
            scanned: order.scanned ?? 0,
            overScanned: order.overScanned ?? 0,
            remaining: order.remaining ?? 0,
            progress: order.progress ?? 0
        };

        const items = Array.isArray(order.items) ? order.items : [];
        if (items.length === 0) {
            // Preserve order even if it has no items yet
            return [{ ...orderFields, sku: '' }];
        }

        return items.map(item => {
            // One row per SKU line, carrying its quantities. The Assigned Asset # / Tag ID
            // columns still show only the first assigned item, so a line of 50 exports as
            // one row reporting 32 / 50 rather than 50 rows. Fanning out per unit would
            // break the round trip through the importer, which reads one row per line.
            const firstAssigned = item.assignedItems?.[0] || {};
            const isOverScannedRow = item.isOverScanned === true;
            const expectedQty = isOverScannedRow ? 0 : (item.expectedQty ?? 1);
            const scannedQty = item.scannedQty ?? (item.assignedItems || []).length;
            return {
                ...orderFields,
                sku: item.sku || '',
                // Legacy lines export as 1 / 0 or 1 / 1 rather than blanks, so a file
                // exported before the backfill still round-trips.
                expectedQty,
                scannedQty,
                'order:fulfillmentStatus': item['order:fulfillmentStatus']?.title || '',
                isOverScanned: item.isOverScanned ? 'Yes' : '',
                assignedAssetNo: firstAssigned['item:item']?.title || '',
                assignedTagId: firstAssigned.tagId || '',
                scannedBy: firstAssigned.scannedBy || '',
                scannedAt: firstAssigned.addedTime || ''
            };
        });
    });

    const timestamp = format(new Date(), "yyyyMMMdd'T'hhmmaaa");

    Object.assign(data, {
        data: denormalizedRecords,
        count: denormalizedRecords.length,
        fileName: `Order Export_${timestamp}`,
        columns: ORDER_EXPORT_COLUMNS
    });

    return payload;
}

const ORDER_EXPORT_COLUMNS = [
    {
        sequence: 0,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Order #',
        propertyName: 'title'
    },
    {
        sequence: 1,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'SKU',
        propertyName: 'sku'
    },
    {
        sequence: 2,
        hNodeType: 'Number',
        hNodeTypeGroup: 'listColumn',
        title: 'Expected Qty',
        propertyName: 'expectedQty'
    },
    {
        sequence: 3,
        hNodeType: 'Number',
        hNodeTypeGroup: 'listColumn',
        title: 'Scanned Qty',
        propertyName: 'scannedQty'
    },
    {
        sequence: 4,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Order Status',
        propertyName: 'order:status'
    },
    {
        sequence: 5,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Item Status',
        propertyName: 'order:fulfillmentStatus'
    },
    {
        sequence: 6,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Over-Scanned',
        propertyName: 'isOverScanned'
    },
    {
        sequence: 7,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Location',
        propertyName: 'location:location'
    },
    {
        sequence: 8,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Assigned Asset #',
        propertyName: 'assignedAssetNo'
    },
    {
        sequence: 9,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Assigned Tag ID',
        propertyName: 'assignedTagId'
    },
    {
        sequence: 10,
        hNodeType: 'Text',
        hNodeTypeGroup: 'listColumn',
        title: 'Scanned By',
        propertyName: 'scannedBy'
    },
    {
        sequence: 11,
        hNodeType: 'LocalVerboseDateTime',
        dataType: 'localVerboseDateTime',
        hNodeTypeGroup: 'listColumn',
        title: 'Scanned At',
        propertyName: 'scannedAt'
    },
    {
        sequence: 12,
        hNodeType: 'Number',
        hNodeTypeGroup: 'listColumn',
        title: 'Scanned',
        propertyName: 'scanned'
    },
    {
        sequence: 13,
        hNodeType: 'Number',
        hNodeTypeGroup: 'listColumn',
        title: 'Over-Scanned Total',
        propertyName: 'overScanned'
    },
    {
        sequence: 14,
        hNodeType: 'Number',
        hNodeTypeGroup: 'listColumn',
        title: 'Remaining',
        propertyName: 'remaining'
    },
    {
        sequence: 15,
        hNodeType: 'Number',
        hNodeTypeGroup: 'listColumn',
        title: 'Progress',
        propertyName: 'progress'
    }
];
